import { useCallback } from "react";
import { useFormHandler } from "shared/hooks";
import { z } from "zod";
import i18n, { i18nNamespace, loadLanguagePacks } from "i18n";
import { IInviteStaffMember } from "shared/components/molecules";

// Ensure the required language packs are loaded
await loadLanguagePacks([i18nNamespace.Core, i18nNamespace.Directory]);

export const inviteStaffMemberSchema = z.object({
  email: z
    .string({
      required_error: `${i18n.t("core:STAFF.INVITE_MODAL.LABELS.EMAIL")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    })
    .trim()
    .min(6, {
      message: `${i18n.t("core:STAFF.INVITE_MODAL.LABELS.EMAIL")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    })
    .email({ message: i18n.t("core:FORM_ERRORS.INVALID_EMAIL") }),
  firstName: z
    .string({
      required_error: `${i18n.t("core:STAFF.INVITE_MODAL.LABELS.FIRST_NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    })
    .trim()
    .min(1, {
      message: `${i18n.t("core:STAFF.INVITE_MODAL.LABELS.FIRST_NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    }),
  lastName: z
    .string({
      required_error: `${i18n.t("core:STAFF.INVITE_MODAL.LABELS.LAST_NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    })
    .trim()
    .min(1, {
      message: `${i18n.t("core:STAFF.INVITE_MODAL.LABELS.LAST_NAME")} ${i18n.t("core:FORM_ERRORS.REQUIRED")}`,
    }),
});

export const inviteStaffMemberFormSchema = z.object({
  sendInvitationEmail: z.boolean(),
  toInvite: z.array(inviteStaffMemberSchema).min(1),
});

export type InviteStaffMemberFormValues = z.infer<
  typeof inviteStaffMemberFormSchema
>;

type UseInviteStaffMemberFormProps = {
  sendInvitationEmail: boolean;
  dataProcessor: (
    sendInvitationEmail: boolean,
    staffToInvite: IInviteStaffMember[],
  ) => Promise<void>;
  onSuccess: () => void;
  onError: (message?: string) => void;
};

export const useInviteStaffMemberForm = ({
  sendInvitationEmail,
  dataProcessor,
  onSuccess,
  onError,
}: UseInviteStaffMemberFormProps) => {
  const defaultValues: InviteStaffMemberFormValues = {
    sendInvitationEmail,
    toInvite: [
      {
        email: "",
        firstName: "",
        lastName: "",
      },
    ],
  };

  return useFormHandler<InviteStaffMemberFormValues>(
    useCallback(
      async (data) => {
        if (data) {
          await dataProcessor(
            data.sendInvitationEmail ?? true, // Default to true if the value is undefined
            (data.toInvite ?? []) as IInviteStaffMember[],
          )
            .then(() => onSuccess())
            .catch(() =>
              onError(
                `${i18n.t("directory:OPERATOR.ADD_OPERATOR_MODAL.FORM_ERRORS.CREATE_USER_INVITE")} ${i18n.t("core:FORM_ERRORS.PLEASE_TRY_AGAIN")}`,
              ),
            );
        }
      },
      [dataProcessor, onError, onSuccess],
    ),
    defaultValues,
    { schema: inviteStaffMemberFormSchema, mode: "onBlur" },
  );
};
